<template>
  <div class="d-flex flex-column ga-4">
    <v-alert type="info" density="comfortable">
      {{ $t("ncrm.workflows.nodeSettings.aiCompatibility.hint") }}
    </v-alert>

    <v-switch
      v-model="form.status"
      :true-value="'active'"
      :false-value="'inactive'"
      color="primary"
      hide-details
      :label="form.status === 'active'
        ? $t('ncrm.workflows.nodeSettings.aiCompatibility.activeOn')
        : $t('ncrm.workflows.nodeSettings.aiCompatibility.activeOff')"
    />

    <v-textarea
      v-model="form.criterion"
      :label="$t('ncrm.workflows.nodeSettings.aiCompatibility.criterion')"
      :hint="$t('ncrm.workflows.nodeSettings.aiCompatibility.criterionHint')"
      persistent-hint
      rows="3"
      auto-grow
    />

    <v-select
      v-model="form.promptKey"
      :items="promptOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.workflows.nodeSettings.aiCompatibility.prompt')"
      :loading="loadingAiPrompts"
      hide-details="auto"
      :menu-props="selectMenuProps"
      @update:model-value="applyPromptProfile"
    />

    <div class="d-flex flex-wrap ga-2">
      <v-chip color="neutral">
        {{ $t("ncrm.workflows.nodeSettings.aiCompatibility.profile") }}: {{ form.aiProfileKey || "—" }}
      </v-chip>
      <v-btn size="small" color="primary" variant="tonal" @click="$emit('open-ai-workspace')">
        <v-icon start>mdi-open-in-new</v-icon>
        {{ $t("ncrm.workflows.actions.openAiWorkspace") }}
      </v-btn>
    </div>

    <v-select
      v-model="form.mode"
      :items="modeOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.workflows.nodeSettings.aiCompatibility.mode')"
      hide-details="auto"
      :menu-props="selectMenuProps"
    />
  </div>
</template>

<script>
export default {
  name: "NcrmWorkflowAiCompatibilitySettings",
  props: {
    settings: { type: Object, default: () => ({}) },
    aiPrompts: { type: Array, default: () => [] },
    loadingAiPrompts: { type: Boolean, default: false },
    selectMenuProps: { type: Object, default: () => ({ maxHeight: 320, zIndex: 2710 }) },
  },
  emits: ["update:value", "open-ai-workspace"],
  data() {
    return { form: this.buildForm(this.settings) };
  },
  computed: {
    promptOptions() {
      const options = (this.aiPrompts || []).map((p) => ({
        value: p.prompt_key,
        label: [p.display_name || p.prompt_key, p.model_key || "-"].join(" · "),
      }));
      if (this.form.promptKey && !options.some((o) => o.value === this.form.promptKey)) {
        options.unshift({ value: this.form.promptKey, label: this.form.promptKey });
      }
      return options;
    },
    modeOptions() {
      return [
        { value: "manual", label: this.$t("ncrm.workflows.nodeSettings.enrichment.modes.manual") },
        { value: "automatic", label: this.$t("ncrm.workflows.nodeSettings.enrichment.modes.automatic") },
      ];
    },
  },
  watch: {
    settings: { deep: true, handler(next) { this.form = this.buildForm(next); this.emitValue(); } },
    form: { deep: true, handler() { this.emitValue(); } },
  },
  mounted() { this.applyPromptProfile(); this.emitValue(); },
  methods: {
    buildForm(settings = {}) {
      return {
        status: settings.status === "active" ? "active" : "inactive",
        mode: settings.mode === "automatic" ? "automatic" : "manual",
        criterion: settings.criterion || "",
        promptKey: settings.promptKey || settings.prompt_key || "ncrm_mailjet_compatibility",
        aiProfileKey: settings.aiProfileKey || settings.ai_profile_key || "enrich_research",
      };
    },
    applyPromptProfile() {
      const prompt = (this.aiPrompts || []).find((p) => p.prompt_key === this.form.promptKey);
      if (prompt?.profile_key) this.form.aiProfileKey = prompt.profile_key;
    },
    emitValue() {
      this.$emit("update:value", {
        status: this.form.status,
        mode: this.form.mode,
        criterion: this.form.criterion,
        promptKey: this.form.promptKey,
        aiProfileKey: this.form.aiProfileKey,
      });
    },
  },
};
</script>
